import { ComboBox, ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { TextArea, TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { VendorUserType } from "auth/VendorAuth/vendorUserSlice";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { filter, findIndex, sortBy } from "lodash";
import { useState } from "react";
import { showApiErrorMessage, showErrorMessage } from "store/errorModalSlice";
import { addVendorQuestion, selectRfi } from "store/rfiSlice";
import RfiElement from "types/RfiElement";
import { SectionGroup, sectionNames } from "../../types/RfiTypes";

interface IQuestionSubmissionProps {
  user?: VendorUserType;
}

interface IQuestionElementOption {
  value: number;
  label: string;
  id: number;
}

export const initialFilterValues = {
  elementId: null,
  questionText: "",
  isFeedback: false,
};

const QuestionSubmission = (props: IQuestionSubmissionProps) => {
  // Constants
  const max = 4000;

  // Hooks
  const rfi = useAppSelector(selectRfi);
  const dispatch = useAppDispatch();

  // State
  const [rfiId, setRfiId] = useState(rfi.rfi?.rfiId);
  const [isFeedback, setIsFeedback] = useState(false);
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [isUpdating, setIsUpdating] = useState(false);
  const [created, setCreated] = useState(false);
  const [initialValues, setInitialValues] = useState(initialFilterValues);
  const [selectedElement, setSelectedElement] =
    useState<IQuestionElementOption | null>(null);
  const [questionText, setQuestionText] = useState("");

  let elementOptions: IQuestionElementOption[] = [
    { value: 0, label: "RFI General Question", id: 0 },
  ];

  const page3 = filter(
    (rfi.questionnaire ?? []).concat(rfi.vendorInfo ?? []),
    (e: RfiElement) => {
      const eSections = e.sections?.find((s: SectionGroup) => {
        return (
          s.sectionGroupName === "Questionnaire" ||
          s.sectionGroupName === "Vendor Information"
        );
      });
      return (
        eSections?.sectionGroupName === "Questionnaire" ||
        eSections?.sectionGroupName === "Vendor Information"
      );
    }
  );

  var result = sortBy(page3, (element) => {
    return element.sections?.find(
      (x: SectionGroup) =>
        x.sectionGroupName === "Questionnaire" ||
        x.sectionGroupName === "Vendor Information"
    )?.sequence;
  });

  result.map((element: RfiElement) => {
    let questionNumber =
      element.sections?.find(
        (x: SectionGroup) => x.sectionGroupName === sectionNames.VENDOR_INFO
      )?.questionNumber ?? 0;

    if (questionNumber === 0) {
      questionNumber =
        element.sections?.find(
          (x: SectionGroup) => x.sectionGroupName === sectionNames.QUESTIONNAIRE
        )?.questionNumber ?? 0;
    }

    let index = findIndex(elementOptions, (option: IQuestionElementOption) => {
      return option.id === element.elementId;
    });

    if (index === -1 && questionNumber != 0) {
      elementOptions.push({
        value: questionNumber,
        label: `${questionNumber}. ${element.fieldLabel}`,
        id: element.elementId,
      });
    }
  });

  elementOptions.sort((a, b) => a.value - b.value);

  // Handlers
  const submitQuestion = (data: any) => {
    setIsUpdating(true);
    // props.setIsUpdateQA && props.setIsUpdateQA(true);
    axios
      .post(`/api/v1/rfi/${rfiId}/VendorQuestions`, data)
      .then((res) => {
        setIsUpdating(false);
        dispatch(
          addVendorQuestion({
            rfxVendorQuestionId: res.data,
            rfiElementId: selectedElement?.id ?? 0,
            questionText: questionText,
            answerStatus: "Unanswered",
            answerId: res.data,
          })
        );
      })
      .catch((error) => {
        setIsUpdating(false);
        // props.setIsUpdateQA && props.setIsUpdateQA(false);
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error submitting Vendor Question - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      });
  };

  const submitFeedback = (data: any) => {
    setIsUpdating(true);
    axios
      .post(`/api/v1/rfi/${rfiId}/VendorFeedbacks`, data)
      .then((res) => {
        setIsUpdating(false);

        dispatch(
          addVendorQuestion({
            rfxVendorQuestionId: res.data,
            rfiElementId: selectedElement?.id ?? 0,
            questionText: questionText,
            answerStatus: "Feedback",
            answerId: res.data,
          })
        );
      })
      .catch((error) => {
        setIsUpdating(false);
        typeof error.response?.data?.[0]?.error?.message === "undefined"
          ? dispatch(
              showErrorMessage(
                `Error submitting Vendor Feedback - ${error.message}`
              )
            )
          : dispatch(showApiErrorMessage({ errorResponse: error }));
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleFeedbackChange = () => {
    setIsFeedback(!isFeedback);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!questionText) {
      dispatch(
        showErrorMessage(`A value is required before submitting a question.`)
      );

      return;
    }

    const rfiElementId =
      selectedElement && selectedElement.id ? selectedElement.id : 0;
    if (isFeedback) {
      let requestData = {
        rfiElementId: rfiElementId,
        feedbackText: questionText,
      };
      submitFeedback(requestData);
    } else {
      let requestData = {
        rfiElementId: rfiElementId,
        questionText: questionText,
      };
      submitQuestion(requestData);
    }
    setQuestionText("");
    setIsFeedback(false);
  };

  const handleElementChange = (event: ComboBoxChangeEvent) => {
    setSelectedElement(event.value);
  };

  const handleQuestionTextChange = (event: TextAreaChangeEvent) => {
    setQuestionText(event.target?.value ?? "");
  };

  const handleClear = () => {
    setInitialValues(initialFilterValues);
  };

  // Render
  return (
    <div>
      <a id="submission" />
      Ask a question/provide feedback about:
      <ComboBox
        id="element-dropdown"
        name="elementId"
        className="w-100 text-black mb-2"
        data={elementOptions}
        defaultValue={elementOptions[0]}
        onChange={handleElementChange}
        textField="label"
        dataItemKey="value"
        clearButton={false}
      />
      Question/Feedback Text
      <TextArea
        id="question-submission-text"
        value={questionText}
        onChange={handleQuestionTextChange}
        maxLength={max}
        rows={7}
      />
      <Hint direction={"end"}>
        {questionText?.length ?? 0} / {max}
      </Hint>
      <div className="form-check form-check-inline">
        <input
          className="form-check-input"
          type="checkbox"
          checked={isFeedback}
          id="isFeedback"
          onChange={handleFeedbackChange}
        />
        <label className="form-check-label" htmlFor="isFeedback">
          Feedback only - government response not required
        </label>
      </div>
      {isUpdating ? (
        <div className="threeBallsFalling"></div>
      ) : (
        <div className="d-flex flex-nowrap justify-content-between align-items-center k-mt-3">
          <div></div>
          <div>
            <button
              className="btn btn-primary mb-4 me-3"
              aria-label="Submit Vendor Question or Feedback"
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionSubmission;
